import { getRouteApi } from '@tanstack/react-router';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BsArrow90DegLeft } from 'react-icons/bs';

import SchufaField from '../../General/SchufaField.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { useSchufaTransfer } from '~/features/leadDetails/hooks/useSchufaTransfer.ts';
import type { SchemaLivingSituationForm } from '~/features/leadDetails/schemas/living.schema.ts';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    addressKind: 'anschrift' | 'vorherigeAnschrift';
    antragsteller: 1 | 2;
};

const AddressSchufaColumn: React.FC<Props> = ({ addressKind, antragsteller }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { t } = useTranslation();
    const { setValue } = useFormContext<SchemaLivingSituationForm>();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const isDisabled = !canExecuteApplicantCommand({
        applicant: antragsteller,
        commandName:
            addressKind === 'vorherigeAnschrift'
                ? LOAN_REQUEST_COMMANDS.changeApplicantPreviousAddress
                : LOAN_REQUEST_COMMANDS.changeApplicantAddress,
        permissions,
    });

    const { schufaCityZip, schufaStreet, transferAddress, transferCityAndZip, transferStreet } = useSchufaTransfer({
        addressKind,
        antragsteller,
        setValue,
    });

    return (
        <div className="min-w-0 flex-1 space-y-2">
            <div className="flex h-8 items-center justify-between gap-2">
                <span className="truncate text-sm font-normal italic text-midnight">
                    {t('PERSONAL_DATA.DATA_FROM_SCHUFA')}
                </span>
                <Button
                    type="button"
                    variant={ButtonVariant.SECONDARY}
                    onClick={transferAddress}
                    disabled={isDisabled}
                    iconLeft={<BsArrow90DegLeft strokeWidth={1} size={15} />}
                >
                    {t('PERSONAL_DATA.TAKE_OVER_ALL_SCHUFA_DATA')}
                </Button>
            </div>
            <SchufaField displayValue={schufaStreet} onTransfer={transferStreet} disabled={isDisabled} />
            <SchufaField displayValue={schufaCityZip} onTransfer={transferCityAndZip} disabled={isDisabled} />
        </div>
    );
};

export default AddressSchufaColumn;
